/**
 * Add Season - Club admin interface for creating new seasons
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import apiService from '../../services/api';
import { AdminPageLayout, FormInput, FormTextArea, FormSelect, FormDatePicker, Button, Section, Card, Grid, Col } from '../../components';
import { FormActionGroup } from '../../components/admin/patterns';
import { useToast } from '../../contexts/ToastContext';
import { useFormValidation } from '../../hooks/useFormValidation';

const AddSeason = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const {
    formData,
    errors,
    touched,
    isSubmitting,
    handleFieldChange,
    handleFieldBlur,
    handleSubmit: handleFormSubmit,
    setFieldValue
  } = useFormValidation({
    initialValues: {
      name: '',
      description: '',
      season_type: 'fall',
      year: new Date().getFullYear(),
      start_date: '',
      end_date: '',
      registration_start: '',
      registration_end: '',
      status: 'planning',
      is_active: true,
      color_code: '#2196F3',
      tags: []
    },
    validationSchema: (values) => {
      const newErrors = {};

      if (!values.name.trim()) {
        newErrors.name = 'Season name is required';
      }

      if (!values.start_date) {
        newErrors.start_date = 'Season start date is required';
      }

      if (!values.end_date) {
        newErrors.end_date = 'Season end date is required';
      }

      if (values.start_date && values.end_date && values.start_date >= values.end_date) {
        newErrors.end_date = 'End date must be after start date';
      }

      if (!values.registration_start) {
        newErrors.registration_start = 'Registration start date is required';
      }

      if (!values.registration_end) {
        newErrors.registration_end = 'Registration end date is required';
      }

      if (values.registration_start && values.registration_end && values.registration_start >= values.registration_end) {
        newErrors.registration_end = 'Registration end date must be after start date';
      }

      return newErrors;
    },
    onSubmit: async (values) => {
      try {
        await apiService.createSeason(values);
        showSuccess('Season created successfully');
        navigate('/admin/seasons');
      } catch (err) {
        showError(err.message || 'Failed to create season');
        throw err;
      }
    }
  });

  const seasonTypes = [
    { value: 'fall', label: 'Fall' },
    { value: 'spring', label: 'Spring' },
    { value: 'summer', label: 'Summer' },
    { value: 'winter', label: 'Winter' },
    { value: 'full_year', label: 'Full Year' },
    { value: 'custom', label: 'Custom' }
  ];

  const statusOptions = [
    { value: 'planning', label: 'Planning' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'archived', label: 'Archived' }
  ];

  return (
    <AdminPageLayout
      title="New Season"
      subtitle="Create a new season for your club"
      backPath="/admin/seasons"
      maxWidth="1400px"
    >
      <form onSubmit={handleFormSubmit}>
        <Section title="BASIC INFORMATION">
          <Card level={1}>
            <FormInput
              label="Season Name"
              name="name"
              value={formData.name}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={touched.name ? errors.name : ''}
              placeholder="e.g., Fall 2024 Season"
              required
            />

            <FormTextArea
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={touched.description ? errors.description : ''}
              placeholder="Optional description"
              rows={3}
            />

            <Grid columns={2} gap="2rem">
              <Col>
                <FormSelect
                  label="Season Type"
                  name="season_type"
                  value={formData.season_type}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.season_type ? errors.season_type : ''}
                  options={seasonTypes}
                  required
                />
              </Col>

              <Col>
                <FormInput
                  label="Year"
                  name="year"
                  type="number"
                  value={formData.year}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.year ? errors.year : ''}
                  min="2024"
                  max="2030"
                  required
                />
              </Col>
            </Grid>
          </Card>
        </Section>

        <Section title="SEASON DATES">
          <Card level={1}>
            <Grid columns={2} gap="2rem">
              <Col>
                <FormDatePicker
                  label="Season Start Date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.start_date ? errors.start_date : ''}
                  required
                />
              </Col>

              <Col>
                <FormDatePicker
                  label="Season End Date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.end_date ? errors.end_date : ''}
                  required
                />
              </Col>
            </Grid>
          </Card>
        </Section>

        <Section title="REGISTRATION PERIODS">
          <Card level={1}>
            <p style={{ marginBottom: '1.5rem', color: 'var(--color-text-secondary)' }}>
              When registration opens and closes for this season
            </p>

            <Grid columns={2} gap="2rem">
              <Col>
                <FormDatePicker
                  label="Registration Start"
                  name="registration_start"
                  value={formData.registration_start}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.registration_start ? errors.registration_start : ''}
                  required
                />
              </Col>

              <Col>
                <FormDatePicker
                  label="Registration End"
                  name="registration_end"
                  value={formData.registration_end}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  error={touched.registration_end ? errors.registration_end : ''}
                  required
                />
              </Col>
            </Grid>
          </Card>
        </Section>

        <Section title="SEASON STATUS">
          <Card level={1}>
            <FormSelect
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={touched.status ? errors.status : ''}
              options={statusOptions}
            />
            <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--surface-lvl2)', borderRadius: '8px', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
              <div style={{ marginBottom: '0.5rem' }}>
                Only one season can be active at a time.
              </div>
              <div>
                <strong>Note:</strong> The active season will automatically be used as the default for creating new calendars and programs.
              </div>
            </div>
          </Card>
        </Section>

        <FormActionGroup
          onCancel={() => navigate('/admin/seasons')}
          saveLabel={isSubmitting ? 'CREATING...' : 'CREATE SEASON'}
          cancelLabel="CANCEL"
          loading={isSubmitting}
          type="submit"
        />
      </form>
    </AdminPageLayout>
  );
};

export default AddSeason;
